
import { gtcChangeColorText, gtcTransitionEnd, gtcScrollLock, gtcScrollUnlock, getPageContentHtml, getSectionHtml, getTemplateHtml, gtcTrapFocus, gtcRemoveTrapFocus, gtcSetPreviousFocus, gtcThrottle } from 'gtcUtils';

/************************* GTC Opener *************************/

/**
 * GTC Opener
 *
 * Settings: if the opener contains "data-gtc-close" and "this" is click "this" will close
 */

//Default classes
/**
 * @type {Object}
 * @property {string} open - The class that is added to the opener when it is open
 * @property {string} close - The class that is added to the opener when it is closed
 * @property {string} overlayOpen - The class that is added to the body when the opener is open
 * @property {string} scrollLock - The class that is added to the body when the opener is open
 */
const classes = {
  open: 'open',
  close: 'close',
  overlayOpen: 'gtc:overlay-open',
  scrollLock: 'gtc:scroll-lock'
}

// Opener array is used to store the openers that are open, this is used to set the z-index of the openers
const openerArray = [];
// Trigger array is used to store the trigger that opened the opener, this is used to set the focus back to the trigger when the opener is closed
// const triggerArray = [];

class GTCOpener extends HTMLElement {
  static observedAttributes = [classes.open];
  #rendered = false;
  #isBusy = false;
  #zIndex = 0;

  constructor() {
    super()

    this.click = this.click.bind(this);
    this.keydown = gtcThrottle(this.keydown.bind(this), 50);
    this.attachShadow({ mode: "open" })
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (this.#isBusy) return;
    try {
      this.#isBusy = true;

      if (name !== classes.open) return;

      if (newValue !== null) {
        if (this.#rendered === false) {
          await this.init();
          this.#isBusy = false;
        }
        else {
          await this.opening();
          this.#isBusy = false;
        }
      } else {
        await this.closing();
      }

    } catch (error) {
      console.error(error);
    } finally {
      this.#isBusy = false;
    }
  }

  get isOpen() {
    return this.getAttribute(classes.open) === "true";
  }
  set isOpen(value) {
    this.setAttribute(classes.open, value ? "true" : "false");
  }

  async connectedCallback() {
    this.settings = {
      scrollLock: this.getAttribute('scroll-lock') === 'false' ? false : true,
      changeColorText: this.hasAttribute('change-color-text') || false,
      canonicalUrl: this.getAttribute('canonical-url') || '',
      appendToElement: this.hasAttribute('append-to-element') ? this.getAttribute('append-to-element') || '' : null,
      triggerElement: null
    }

    await this.appendToElement();

    // Add accessibility attributes
    this.setAttribute('aria-hidden', !this.isOpen);
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
  }

  disconnectedCallback() {
    this.close();
  }

  async init() {
    if (this.#rendered === true) {
      this.open();
    } else {
      await this.create();
    }
  }

  async appendToElement() {

    if (this.settings.appended) return;
    if (this.settings.appendToElement === null) return;

    const target = this.settings.appendToElement !== '' ? document.querySelector(this.settings.appendToElement) : null;

    this.settings.appended = true;
    this.removeAttribute('append-to-element');

    if (target) {
      target.appendChild(this)
    } else {
      document.body.appendChild(this);
    }
  }

  addListeners() {
    this.addEventListener('click', this.click, false);
    document.addEventListener("keydown", this.keydown, false);
  }

  removeListeners() {
    this.removeEventListener('click', this.click, false);
    document.removeEventListener("keydown", this.keydown, false);
  }

  click(event) {
    const shadowDomTarget = event.composedPath()[0];
    if (this.isNotOverlayOrCloseButton(this, shadowDomTarget)) {
      return;
    }

    this.close();
  }

  isNotOverlayOrCloseButton(element, target) {
    return (element.shadowRoot.contains(target) || element.contains(target))
      && !target.closest('[part="gtc-overlay"]')
      && !target.closest('[data-close]');
  }

  /**
   * @param {KeyboardEvent} event 
   */
  keydown(event) {
    if (event.key !== 'Escape') return;
    event.stopPropagation();

    // if (openerArray.length > 0) {
    //   const lastOpener = openerArray[openerArray.length - 1];

    //   if (lastOpener === this) {
    //     this.close();
    //   }

    // } else {
    //   this.close();
    // }

    if (openerArray.length === 0 || openerArray[openerArray.length - 1] === this) {
      this.close();
    }
  }

  open(triggerElement = null) {
    if (triggerElement) {
      this.settings.triggerElement = triggerElement;
    }

    this.setAttribute(classes.open, '');
  }

  close() {
    this.removeAttribute(classes.open);
  }

  opening() {

    // push the opener to the array
    if (!(openerArray[openerArray.length - 1] === this)) {
      openerArray.push(this);
    }


    // Set the z-index of the opener, to ensure it is on top of the other openers
    if (this.#zIndex === 0) {
      const styles = window.getComputedStyle(this);
      this.#zIndex = parseInt(styles.zIndex, 10);
    }

    this.style.zIndex = this.#zIndex + openerArray.length;

    this.style.display = 'block';
    this.shadowRoot.tabIndex = 0;

    //This forces repaint
    void this.offsetHeight;

    // Set scroll lock
    if (this.settings.scrollLock === true) gtcScrollLock([classes.scrollLock]);

    // Add the opener event listeners, after the repaint, to ensure the event listeners are added after the display block
    requestAnimationFrame(() => {
      this.addListeners();
      this.setAttribute('aria-hidden', "false");
      // Set the focus to the first focusable element in the opener
      gtcTrapFocus(this.shadowRoot);
    });
  }

  closing() {
    let contentPart = this.shadowRoot.querySelector('[part="gtc-content"]');

    // Remove this from the opener array
    openerArray.splice(openerArray.indexOf(this), 1);

    // Remove the opener event listeners
    this.removeListeners();

    this.setAttribute('aria-hidden', "true");

    // Add the transition end event listener, to ensure the content is hidden after the transition ends
    gtcTransitionEnd(contentPart, () => {
      this.style.display = 'none';
      this.style.zIndex = this.#zIndex
    });

    this.shadowRoot.tabIndex = -1;

    // Remove the scroll lock if there are no openers in the openerArray
    if (openerArray.length === 0) {
      gtcScrollUnlock([classes.scrollLock]);
    }

    // Set the focus back to the trigger element
    if (this.settings.triggerElement) {
      gtcSetPreviousFocus(this.settings.triggerElement);
      this.settings.triggerElement = null;
    }

    // Remove the trap focus behavior from the opener
    gtcRemoveTrapFocus(this);
  }

  async getData() {
    const slots = this.querySelectorAll('[slot]');

    if (slots.length) {
      const contentDataPromises = Array.from(slots).map(async (slot) => {
        await this.getDataByAttributes(slot);
      });

      await Promise.all(contentDataPromises);

    }
  }

  async getDataByAttributes(slot) {
    const data = {};

    for (let i = 0; i < slot.attributes.length; i++) {
      const { name: attributeName, value: attributeValue } = slot.attributes[i];

      if (attributeName) {
        const key = attributeName;

        switch (key) {
          case 'page':
            const pageContent = await getPageContentHtml(attributeValue);
            slot.insertAdjacentHTML('beforeend', pageContent);
            break;
          case 'section':
            const sectionContent = await getSectionHtml(attributeValue, this.settings.canonicalUrl);
            slot.insertAdjacentHTML('beforeend', sectionContent);
            break;
          case 'template':
            const templateContent = await getTemplateHtml(attributeValue);
            slot.insertAdjacentHTML('beforeend', templateContent);
            break;
          default:
        };
      }
    }
    return data;
  }


  async create() {

    // If the opener has a shadow dom template, attach the shadow dom
    const templateDom = document.getElementById(this.shadowDomTemplate);
    if (templateDom) {
      this.shadowRoot.appendChild(templateDom.content.cloneNode(true));
    } else {
      console.warn('Shadow DOM could not be attached: template not found');
    }

    await this.getData();

    this.shadowRoot.querySelectorAll('slot').forEach((slot) => {
      // if slot is empty remove it
      if (!slot.assignedElements({ flatten: true }).length) {
        slot.remove();
      }
    });

    this.shadowRoot.querySelectorAll('[part="gtc-content"] [part]:not([part="gtc-header"] [part])').forEach((part) => {
      // if part is empty remove it
      if (!part.children.length) {
        part.remove();
      }
    });

    if (!this.innerHTML) return console.warn('No HTML IN GTC-OPENER!');

    if (this.settings?.class) {
      const classes = this.settings.class.split(' ').filter((letter) => letter !== "");
      classesList.add(...classes)
    }


    // This is used to change the color of the text in the opener
    if (this.settings.changeColorText) {
      gtcChangeColorText(this);
    }

    this.#rendered = true
    this.opening();
  }

}

/************************* GTC Modal *************************/

const gtcModal = class GTCModal extends GTCOpener {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
  }

  get shadowDomTemplate() {
    return this.getAttribute("template") || "gtc-modal-template";
  }
}

if (!window.customElements.get("gtc-modal")) {
  window.customElements.define("gtc-modal", gtcModal);
}

/************************* GTC Drawer *************************/

const gtcDrawer = class GTCDrawer extends GTCOpener {

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  get shadowDomTemplate() {
    return this.getAttribute("template") || "gtc-drawer-template";
  }
}

if (!window.customElements.get("gtc-drawer")) {
  window.customElements.define("gtc-drawer", gtcDrawer);
}

/************************* GTC Trigger *************************/

const gtcTrigger = class extends HTMLElement {

  #isBusy = false;

  constructor() {
    super();
    this.click = this.click.bind(this);
    this.keydown = gtcThrottle(this.keydown.bind(this), 50);
  }

  connectedCallback() {
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', 0);
    }
    this.addListeners();
  }

  disconnectedCallback() {
    this.removelisterners();
  }


  addListeners() {
    this.addEventListener('click', this.click, false);
    this.addEventListener("keydown", this.keydown, false);
  }

  removelisterners() {
    this.removeEventListener('click', this.click, false);
    this.removeEventListener("keydown", this.keydown, false);
  }

  keydown(event) {
    if (this.#isBusy) return;
    if (event.key === 'Enter') {
      this.open();
    }
  }

  click() {
    if (this.#isBusy) return;
    this.open();
  }

  open() {
    this.#isBusy = true;

    gtcSetPreviousFocus(this);

    if (this.hasAttribute('aria-controls')) {
      const target = document.getElementById(this.getAttribute('aria-controls'));
      if (target) {
        if (!target.hasAttribute('open')) {
          target.open(this);
        }
      } else {
        this.removelisterners();
        this.setAttribute('disabled', '');
        console.warn(`No target found with ID: ${ this.getAttribute('aria-controls') }`);
      }
    }

    this.#isBusy = false;
  }
};

if (!window.customElements.get("gtc-trigger")) {
  window.customElements.define("gtc-trigger", gtcTrigger);
}

const gtcOpenerLinks = class extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.dom = {};
    this.settings = {
      type: this.getAttribute('type') || 'drawer',
      template: this.getAttribute('template') || 'gtc-drawer-template',
      templateClasses: this.getAttribute('template-classes') || 'gtc:drawer',
      appendToElement: this.hasAttribute('append-to-element') ? this.getAttribute('append-to-element') || '' : null,
      scrollLock: this.getAttribute('scroll-lock') === 'false' ? false : true,
    }

    // if DOMContentLoaded has already happened
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      this.init();
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        this.init();
      });
    }
  }


  init() {
    if (this.settings.appendToElement) {
      this.dom.append = document.querySelector(this.settings.appendToElement);
    }

    this.checkLinks();
  }

  checkLinks() {
    const baseUrl = window.location.origin;
    this.querySelectorAll('a').forEach((link) => {
      const url = link.getAttribute('href');

      if (url.includes(baseUrl) || url.startsWith('/')) {
        if (url.includes('policies/') || url.includes('pages/')) {
          this.tranformToOpener(link);
        }
      }
    });
  }

  async tranformToOpener(link) {
    const randomId = Date.now();

    // Opener
    const opener = document.createElement(`gtc-${ this.settings.type }`);
    const slotTitle = document.createElement('div');
    const slotBody = document.createElement('div');

    // Trigger
    const trigger = document.createElement('gtc-trigger');
    trigger.innerHTML = link.innerHTML;
    trigger.classList.add('inline-link');
    trigger.setAttribute('aria-controls', `Opener-${ randomId }`);
    trigger.setAttribute('tabindex', 0);
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-label', link.title);
    trigger.setAttribute('title', link.title);

    link.replaceWith(trigger);


    opener.id = `Opener-${ randomId }`;
    opener.setAttribute('template', this.settings.template);
    opener.setAttribute('class', this.settings.templateClasses);
    if (this.settings.scrollLock === false) {
      opener.setAttribute('scroll-lock', 'false');
    }

    slotTitle.setAttribute('slot', 'gtc-title');
    slotTitle.innerHTML = link.title;

    slotBody.setAttribute('slot', 'gtc-body');
    slotBody.setAttribute('page', link.href);

    opener.appendChild(slotTitle);
    opener.appendChild(slotBody);

    if (this.dom.append) {
      this.dom.append.appendChild(opener)
    } else if (this.settings.appendToElement === '') {
      document.body.appendChild(opener);
    }
  }
}

if (!window.customElements.get("gtc-opener-links")) {
  window.customElements.define("gtc-opener-links", gtcOpenerLinks);
}

export { gtcModal, gtcDrawer };